<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('textReplace.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('textReplace.title') }}</h1>
      <p>{{ $t('textReplace.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="replace-controls">
        <div class="replace-row">
          <label>{{ $t('textReplace.findLabel') }}</label>
          <input type="text" v-model="findText" :placeholder="$t('textReplace.findPlaceholder')" />
        </div>
        <div class="replace-row">
          <label>{{ $t('textReplace.replaceLabel') }}</label>
          <input type="text" v-model="replaceText" :placeholder="$t('textReplace.replacePlaceholder')" />
        </div>
      </div>

      <div class="options">
        <label class="option-label">
          <input type="checkbox" v-model="ignoreCase" />
          {{ $t('textReplace.ignoreCase') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="useRegex" />
          {{ $t('textReplace.useRegex') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="replaceAll" />
          {{ $t('textReplace.replaceAll') }}
        </label>
      </div>

      <div class="actions">
        <button class="btn primary" @click="doReplace">{{ $t('textReplace.replace') }}</button>
        <button class="btn" @click="copyResult" :disabled="!output">{{ $t('common.copyResult') }}</button>
        <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
      </div>

      <div v-if="errorMessage" class="alert error">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('textReplace.inputLabel') }}</label>
          <textarea v-model="input" :placeholder="$t('textReplace.inputPlaceholder')" spellcheck="false"></textarea>
        </div>
        <div class="editor-panel">
          <label>{{ $t('textReplace.outputLabel') }}
            <span v-if="replaceCount >= 0" class="stats-info">{{ $t('textReplace.countInfo').replace('{n}', replaceCount) }}</span>
          </label>
          <textarea v-model="output" readonly :placeholder="$t('textReplace.outputPlaceholder')" spellcheck="false"></textarea>
        </div>
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('textReplace.whatIs') }}</h2>
      <p>{{ $t('textReplace.whatIsDesc') }}</p>
      <h2>{{ $t('textReplace.useCasesTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('textReplace.useCases')" :key="i">{{ item }}</li>
      </ul>
      <h2>{{ $t('textReplace.regexTitle') }}</h2>
      <p>{{ $t('textReplace.regexDesc') }}</p>
      <ul>
        <li v-for="(item, i) in $t('textReplace.regexExamples')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextReplace',
  data() {
    return {
      input: '',
      output: '',
      findText: '',
      replaceText: '',
      ignoreCase: false,
      useRegex: false,
      replaceAll: true,
      replaceCount: -1,
      errorMessage: '',
      successMessage: ''
    }
  },
  methods: {
    doReplace() {
      this.errorMessage = ''
      this.successMessage = ''
      if (!this.input) return
      if (!this.findText) { this.errorMessage = this.$t('textReplace.emptyFind'); return }

      try {
        let count = 0
        let result = this.input

        if (this.useRegex) {
          const flags = (this.replaceAll ? 'g' : '') + (this.ignoreCase ? 'i' : '')
          const regex = new RegExp(this.findText, flags)
          result = this.input.replace(regex, () => { count++; return this.replaceText })
        } else {
          if (this.replaceAll) {
            const flags = this.ignoreCase ? 'gi' : 'g'
            const escaped = this.findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            const regex = new RegExp(escaped, flags)
            result = this.input.replace(regex, () => { count++; return this.replaceText })
          } else {
            if (this.ignoreCase) {
              const idx = this.input.toLowerCase().indexOf(this.findText.toLowerCase())
              if (idx >= 0) {
                result = this.input.substring(0, idx) + this.replaceText + this.input.substring(idx + this.findText.length)
                count = 1
              }
            } else {
              const idx = this.input.indexOf(this.findText)
              if (idx >= 0) {
                result = this.input.substring(0, idx) + this.replaceText + this.input.substring(idx + this.findText.length)
                count = 1
              }
            }
          }
        }

        this.output = result
        this.replaceCount = count
        this.successMessage = this.$t('textReplace.success')
        setTimeout(() => { this.successMessage = '' }, 2000)
      } catch (e) {
        this.errorMessage = this.$t('textReplace.regexError') + ': ' + e.message
      }
    },
    async copyResult() {
      if (!this.output) return
      try { await navigator.clipboard.writeText(this.output); this.successMessage = this.$t('common.copied') }
      catch { this.successMessage = this.$t('common.copyFailed') }
      setTimeout(() => { this.successMessage = '' }, 2000)
    },
    clearAll() {
      this.input = ''; this.output = ''; this.findText = ''; this.replaceText = ''
      this.replaceCount = -1; this.errorMessage = ''; this.successMessage = ''
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.replace-controls { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
@media (max-width: 768px) { .replace-controls { grid-template-columns: 1fr; } }
.replace-row label { display: block; font-size: 14px; color: var(--text-secondary); margin-bottom: 6px; }
.replace-row input { width: 100%; padding: 10px 14px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; color: var(--text-primary); font-size: 14px; box-sizing: border-box; transition: border-color 0.3s; }
.replace-row input:focus { outline: none; border-color: var(--accent-color); }
.options { display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 16px; }
.option-label { display: flex; align-items: center; gap: 6px; color: var(--text-secondary); font-size: 14px; cursor: pointer; }
.actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.btn { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 20px; cursor: pointer; font-size: 14px; transition: all 0.2s; }
.btn:hover { background: var(--bg-hover); }
.btn.primary { background: var(--accent-color); border-color: var(--accent-color); color: white; }
.btn.primary:hover { background: var(--accent-hover); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.alert { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; }
.alert.error { background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.3); color: var(--error-color); }
.alert.success { background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.editor-container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
@media (max-width: 768px) { .editor-container { grid-template-columns: 1fr; } }
.editor-panel { display: flex; flex-direction: column; }
.editor-panel label { font-size: 14px; color: var(--text-secondary); margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
.stats-info { font-size: 12px; color: var(--accent-color); }
.editor-panel textarea { min-height: 300px; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; color: var(--text-primary); font-family: 'Monaco','Menlo','Ubuntu Mono',monospace; font-size: 13px; line-height: 1.5; resize: vertical; width: 100%; box-sizing: border-box; }
.editor-panel textarea:focus { outline: none; border-color: var(--accent-color); }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
</style>
